// The shopping trip: what the user did, and nothing more.
//
// This is the smallest state machine that can hold the one fact this release
// is entitled to hold — *the user says they went and got the food*. Read the
// transitions below and notice what is absent: there is no order, no basket,
// no total, no pickup slot, no confirmation number, and no way to reach
// `picked-up` except a person pressing a button that says so.
//
// That absence is the feature. Opening a retailer's website tells us the user
// opened a website; it does not tell us they ordered anything, and a browser
// cannot find out. An app that inferred "picked up" from "tab was opened"
// would be wrong for everyone who looked at the site and changed their mind —
// and it would be wrong silently, in the one place the user is relying on it.
//
// `startedAt` is therefore "when they opened the store's site", not "when the
// order was placed", and `completedAt` is "when they told us", not "when the
// order was ready". The names say what they mean.

export const TRIP_NONE = 'none';
export const TRIP_SHOPPING = 'shopping';
export const TRIP_PICKED_UP = 'picked-up';

export const EMPTY_TRIP = {
  status: TRIP_NONE,
  storeId: null,
  storeName: null,
  startedAt: null,
  completedAt: null,
};

export function clearTrip() {
  return { ...EMPTY_TRIP };
}

function storeIdOf(store) {
  return store?.id ?? store?.name ?? null;
}

// Whether a trip is about the store currently selected. A trip that isn't gets
// treated as stale by the screen rather than shown against the wrong shop —
// "you picked this up at Tesco" is a lie if the user has since switched to
// Lidl, and it is the kind of lie the user cannot detect.
export function isTripFor(trip, store) {
  if (!trip || trip.status === TRIP_NONE) return false;
  const id = storeIdOf(store);
  return Boolean(id) && trip.storeId === id;
}

// The user opened the store's website. Re-opening the same store's site is the
// same trip continuing — people check the site twice — so the original
// `startedAt` survives. A different store is a different trip, and starts over
// rather than inheriting a completion that belonged to somewhere else.
export function startTrip(trip, store, now = Date.now()) {
  const id = storeIdOf(store);
  if (!id) return trip ?? clearTrip();

  const continuing = trip?.status === TRIP_SHOPPING && trip.storeId === id;
  return {
    status: TRIP_SHOPPING,
    storeId: id,
    storeName: store?.name ?? null,
    startedAt: continuing ? trip.startedAt : now,
    completedAt: null,
  };
}

// Only ever called from an explicit "I've picked this up" press, and only
// valid from `shopping`. Completing a trip that was never started would be
// recording a pickup we have no basis for at all, so it is refused rather
// than invented.
export function completeTrip(trip, now = Date.now()) {
  if (trip?.status !== TRIP_SHOPPING) return trip ?? clearTrip();
  return { ...trip, status: TRIP_PICKED_UP, completedAt: now };
}

// The way back for a user who pressed it by mistake, or who didn't end up
// going. Marking a pickup must be as undoable as making it was easy.
export function reopenTrip(trip) {
  if (trip?.status !== TRIP_PICKED_UP) return trip ?? clearTrip();
  return { ...trip, status: TRIP_SHOPPING, completedAt: null };
}
